import React from 'react'

import { ArrowDown, ArrowLeft, ArrowUp, Info, Minus } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Badge } from '~/client/src/shared/components/shadcn-ui/badge'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { WidgetId } from '../../Constants/WidgetConfig'
import { getWidgetDescription } from '../../Constants/WidgetDescriptions'

interface ComparisonData {
  percentageChange: number
  isPositive: boolean
  difference: number
}

interface IMetricWidgetProps {
  widgetId: WidgetId
  title: string
  value: string | number
  label: string
  comparison?: ComparisonData | null
  isFlipped: boolean
  onFlip: (widgetId: string, e: React.MouseEvent) => void
}

const ComparisonIndicator: React.FC<{ comparison: ComparisonData }> = ({
  comparison,
}) => {
  const { percentageChange, isPositive, difference } = comparison

  if (percentageChange === 0 && difference === 0) {
    return (
      <Badge variant="muted" className="tw:mt-3">
        <Minus data-icon="inline-start" />
        {Localization.translator.dashboard.noChange}
      </Badge>
    )
  }

  const formattedPercentage = Math.abs(percentageChange).toFixed(1)
  const absDifference = Math.abs(difference)
  const formattedDifference =
    absDifference < 1 && absDifference > 0
      ? absDifference.toFixed(2)
      : Math.round(absDifference)

  return (
    <Badge variant={isPositive ? 'success' : 'destructive'} className="tw:mt-3">
      {isPositive ? (
        <ArrowUp data-icon="inline-start" />
      ) : (
        <ArrowDown data-icon="inline-start" />
      )}
      {isPositive ? '+' : '-'}
      {formattedDifference} ({formattedPercentage}%)
    </Badge>
  )
}

const MetricWidget: React.FC<IMetricWidgetProps> = ({
  widgetId,
  title,
  value,
  label,
  comparison,
  isFlipped,
  onFlip,
}) => {
  const description = getWidgetDescription(widgetId)

  const content = (
    <div className="widget metric-widget">
      <div className="widget-header">
        <span className="widget-title">{title}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={e => onFlip(widgetId, e)}
          onMouseDown={e => e.stopPropagation()}
          title={Localization.translator.dashboard.widgetInfo}
        >
          <Info />
        </Button>
      </div>
      <div className="widget-body">
        <div className="metric-value">{value}</div>
        <div className="metric-label">{label}</div>
        {comparison && <ComparisonIndicator comparison={comparison} />}
      </div>
    </div>
  )

  return (
    <div className={`widget-flip-container ${isFlipped ? 'flipped' : ''}`}>
      <div className="widget-flip-inner">
        {/* Front side */}
        <div className="widget-flip-front">{content}</div>

        {/* Back side */}
        <div className="widget-flip-back">
          <div className="widget metric-widget">
            <div className="widget-header">
              <span className="widget-title">{title}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={e => onFlip(widgetId, e)}
                onMouseDown={e => e.stopPropagation()}
                title={Localization.translator.dashboard.backToWidget}
              >
                <ArrowLeft />
              </Button>
            </div>
            <div className="widget-body widget-description">
              <p>{description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MetricWidget
